export const INFLUENCER_ACTIVATION_REFRESH_EVENT = 'millet:influencer-activation-refresh';

/** Call after creator profile or assets change so checklist / gates can refetch `/api/influencer/activation`. */
export function requestInfluencerActivationRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(INFLUENCER_ACTIVATION_REFRESH_EVENT));
  }
}

/** Keep in sync with server `CREATOR_MARKETPLACE_READY_TASK_IDS` (influencerActivation.service.js). */
export const INFLUENCER_MARKETPLACE_READY_TASK_IDS = Object.freeze([
  'quick_onboarding',
  'creator_name',
  'social_handles',
  'about',
  'content_categories',
  'content_deliverables',
  'profile_photo',
  'portfolio',
  'rates',
]);

export const INFLUENCER_APPLY_REQUIRED_TASK_IDS = Object.freeze([
  'creator_name',
  'social_handles',
  'about',
  'content_deliverables',
  'profile_photo',
  'portfolio',
  'shipping_address',
]);

export const INFLUENCER_APPLY_OPTIONAL_TASK_IDS = Object.freeze([
  'content_categories',
  'rates',
  'quick_onboarding',
  'verify_tiktok',
  'live_shopping_clip',
  'past_brands',
]);

export function getCreatorApplyRequiredTaskIds(requiresShipping = false) {
  const requiredTaskIds = [...INFLUENCER_APPLY_REQUIRED_TASK_IDS];
  if (!requiresShipping) {
    return requiredTaskIds.filter((id) => id !== 'shipping_address');
  }
  return requiredTaskIds;
}

export function areInfluencerMarketplaceTasksComplete(activation) {
  if (!activation?.tasks?.length) return false;
  const map = new Map(activation.tasks.map((task) => [task.id, task]));
  return INFLUENCER_MARKETPLACE_READY_TASK_IDS.every((id) => map.get(id)?.done);
}

export function getIncompleteInfluencerMarketplaceTasks(activation) {
  const map = new Map((activation?.tasks || []).map((task) => [task.id, task]));
  return INFLUENCER_MARKETPLACE_READY_TASK_IDS.map((id) => map.get(id)).filter(
    (task) => task && !task.done,
  );
}

export function getIncompleteApplyRequiredTasks(activation, requiresShipping = false) {
  const map = new Map((activation?.tasks || []).map((task) => [task.id, task]));
  return getCreatorApplyRequiredTaskIds(requiresShipping)
    .map((id) => map.get(id))
    .filter((task) => task && !task.done);
}

export function areApplyRequiredTasksComplete(activation, requiresShipping = false) {
  if (!activation?.tasks?.length) return false;
  return getIncompleteApplyRequiredTasks(activation, requiresShipping).length === 0;
}

export function sortTasksIncompleteFirst(tasks = []) {
  return [...tasks].sort((a, b) => {
    if (a.done === b.done) return 0;
    return a.done ? 1 : -1;
  });
}

export function partitionActivationTasks(activation) {
  const requiredIds = new Set(INFLUENCER_APPLY_REQUIRED_TASK_IDS);
  const optionalIds = new Set(INFLUENCER_APPLY_OPTIONAL_TASK_IDS);
  const tasks = activation?.tasks || [];

  return {
    required: sortTasksIncompleteFirst(tasks.filter((task) => requiredIds.has(task.id))),
    optional: sortTasksIncompleteFirst(tasks.filter((task) => optionalIds.has(task.id))),
  };
}

export function getInfluencerChecklistProgress(activation) {
  if (!activation?.tasks?.length) return 0;

  const { required, optional } = partitionActivationTasks(activation);
  const requiredComplete = required.filter((task) => task.done).length;
  const optionalComplete = optional.filter((task) => task.done).length;
  const requiredTotal = required.length;
  const optionalTotal = optional.length;
  const optionalRemaining = optional.filter((task) => !task.done).length;
  const requiredProgressPercent = requiredTotal > 0
    ? Math.round((requiredComplete / requiredTotal) * 100)
    : 100;

  if (optionalRemaining > 0 && requiredProgressPercent === 100 && optionalTotal > 0) {
    return Math.round(((requiredComplete + optionalComplete) / (requiredTotal + optionalTotal)) * 100);
  }

  return requiredProgressPercent;
}

export function isInfluencerChecklistComplete(activation) {
  if (!activation || activation.dismissed) return true;

  const { optional } = partitionActivationTasks(activation);
  const optionalRemaining = optional.filter((task) => !task.done).length;
  const incompleteRequired = getIncompleteApplyRequiredTasks(activation, false);

  return incompleteRequired.length === 0 && optionalRemaining === 0;
}

export function getInfluencerChecklistVisibleTasks(activation, visibleTaskLimit = 7) {
  if (!activation?.tasks?.length) return [];

  const { required, optional } = partitionActivationTasks(activation);
  const incompleteRequired = required.filter((task) => !task.done);
  const incompleteOptional = optional.filter((task) => !task.done);

  return [
    ...incompleteRequired,
    ...incompleteOptional,
  ].slice(0, visibleTaskLimit);
}

export function getInfluencerChecklistNextTask(activation) {
  if (!activation?.tasks?.length) return undefined;

  const incompleteRequired = getIncompleteApplyRequiredTasks(activation, false);
  const nextRequired = incompleteRequired.find((task) => !task.locked);
  if (nextRequired) return nextRequired;

  const { optional } = partitionActivationTasks(activation);
  return optional.find((task) => !task.done && !task.locked);
}

export function shouldAutoCollapseInfluencerChecklist(activation) {
  return getIncompleteApplyRequiredTasks(activation, false).length === 0;
}
